export class ApiError extends Error {
  status: number;
  data: unknown;
  cause: unknown;

  constructor(message: string, status = 0, data: unknown = null, cause: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.cause = cause;
  }
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function normalizePath(path: string): string {
  if (!path) {
    return '/';
  }

  return path.startsWith('/') ? path : `/${path}`;
}

export function resolveApiUrl(path: string): string {
  const normalizedPath = normalizePath(path);
  const env = (import.meta as ImportMeta & { env: Record<string, string> }).env;
  const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  const devApiBaseUrl = env.VITE_API_BASE_URL || processEnv?.VITE_API_BASE_URL || '';
  const isProduction = import.meta.env.MODE === 'production' || processEnv?.NODE_ENV === 'production';

  if (isAbsoluteHttpUrl(path)) {
    return path;
  }

  if (isProduction) {
    return normalizedPath;
  }

  if (!devApiBaseUrl) {
    return normalizedPath;
  }

  const trimmedBase = devApiBaseUrl.replace(/\/+$/, '');
  const originOnlyBase = trimmedBase.endsWith('/api') ? trimmedBase.slice(0, -4) : trimmedBase;

  return `${originOnlyBase}${normalizedPath}`;
}

export function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length < 2) {
    return '';
  }

  return parts.pop()?.split(';').shift() || '';
}

export async function apiFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(init.headers || {});
  const csrf = getCookie('csrf');

  if (csrf && !headers.has('X-CSRF-Token')) {
    headers.set('X-CSRF-Token', csrf);
  }

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const requestUrl = resolveApiUrl(path);

  return fetch(requestUrl, {
    ...init,
    headers,
    credentials: 'include',
  });
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function requestJson<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await apiFetch(path, init);
  } catch (error) {
    throw new ApiError('Network request failed', 0, null, error);
  }

  const data = await parseJson(response);

  if (!response.ok) {
    throw new ApiError(
      `Request failed with status ${response.status}`,
      response.status,
      data
    );
  }

  return data as T;
}
